import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';
import Delta from 'quill-delta';
import styled from 'styled-components';

import * as graphql from 'generated/graphql';
import { Modal, Paragraph, QuillInput, QuillText } from '@treams/treams-ui';
import { FieldContainer } from 'packages/kakapo-ui-kit/components/forms/controls/Field';
import Label from 'packages/kakapo-ui-kit/components/forms/controls/Label';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import FormlessField from 'packages/kakapo-ui-kit/components/forms/controls/FormlessField';

import FeedbackComposer from '../composer/FeedbackComposer';

import FeedbackTemplateSelector from './FeedbackTemplateSelector';
import { hasRequiredQuestions } from './FeedbackRequestValidation';

const Error = styled(Box)`
	color: ${(props) => props.theme.colors.red};
`;

export default function FeedbackRequestModalQuestions(props: {
	method: 'template' | 'custom' | 'competencies' | 'goals';
	userID: string;
	instruction?: Delta;
	needsTemplate?: boolean;
	title?: string;
	template?: graphql.FeedbackTemplate;
	questions: (graphql.FeedbackRequestQuestionInput & { id: string })[];
	goals: { goalID: string; text?: string; order: number }[];
	competencies: { competencyID: string; text?: string }[];
	onChange: (
		questions: (graphql.FeedbackRequestQuestionInput & { id: string })[],
		goals: { goalID: string; text?: string; order: number }[],
		competencies: { competencyID: string; text?: string }[],
		title?: string,
		instruction?: Delta,
		template?: graphql.FeedbackTemplate,
	) => void;
	onBack: () => void;
	onDone: () => void;
	returnURL: string;
	minimumQuestions?: number;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const [shouldValidate, setShouldValidate] = useState(false);

	const onChange = (
		questions: (graphql.FeedbackRequestQuestionInput & { id: string })[],
		goals: { goalID: string; text?: string; order: number }[],
		competencies: { competencyID: string; text?: string }[],
		title?: string,
		instruction?: Delta,
		template?: graphql.FeedbackTemplate,
	) => {
		setShouldValidate(false);
		props.onChange(questions, goals, competencies, title, instruction, template);
	};

	const validationError = hasRequiredQuestions(
		props.questions,
		props.goals,
		props.competencies,
		props.minimumQuestions ?? 1,
	);

	return (
		<Modal>
			<Modal.Header title={t('common.feedback.setupQueriesFromScratch')} />

			<Modal.Contents>
				<FieldContainer>
					{props.needsTemplate && (
						<>
							<FeedbackTemplateSelector
								template={props.template}
								onChange={(template) =>
									onChange(
										template?.questions.map(({ question, instruction }) => ({
											id: Math.random().toString(),
											type: question.type,
											text: localize(question.text).text,
											description: localize(instruction ?? [])?.text,
											categoryID: question.category.id,
										})) ?? [],
										props.goals,
										props.competencies,
										props.title,
										props.instruction,
										template,
									)
								}
							/>
							{props.template?.description && (
								<Paragraph mt={2}>
									<QuillText value={localize(props.template.description)?.text} />
								</Paragraph>
							)}
						</>
					)}
				</FieldContainer>
				<FieldContainer>
					<Label title={t('common.feedback.composer.title')} />
					<FormlessField
						value={
							props.title ?? (props.template ? localize(props.template.title).text : undefined)
						}
						onChange={(event) =>
							onChange(
								props.questions,
								props.goals,
								props.competencies,
								event.currentTarget.value,
								props.instruction,
								props.template,
							)
						}
					/>
				</FieldContainer>

				<FieldContainer>
					<Label title={t('common.feedback.composer.instructionalMessage')} />

					<QuillInput
						// Refresh Quill input when feedback template is changed.
						// This input should have the default value of the instruction from a feedback template.
						//
						// When the cycle has a predefined instruction, this will be passed as `props.instruction`
						// and thus will have priority over the feedback template instruction.
						//
						// The behaviour is as follows: when switching feedback templates, it loads all the new values
						// whenever a value is changed, this will become `props.instruction` and thus becomes the new
						// value, even if the template is changed.
						key={props.template?.id ?? 'template.none'}
						value={
							props.instruction || localize(props.template?.instruction ?? [])?.text || { ops: [] }
						}
						onChange={(value) =>
							onChange(
								props.questions,
								props.goals,
								props.competencies,
								props.title,
								value,
								props.template,
							)
						}
					/>
				</FieldContainer>

				{(!props.needsTemplate || props.template) && (
					<>
						<FeedbackComposer
							method={props.method}
							userID={props.userID}
							autoFocus={!props.needsTemplate}
							questions={props.questions}
							goals={props.goals}
							competencies={props.competencies}
							onChange={(questions, goals, competencies) =>
								onChange(
									questions,
									goals,
									competencies,
									props.title,
									props.instruction,
									props.template,
								)
							}
						/>
					</>
				)}

				{shouldValidate && (
					<>
						{validationError === 'notMeetingMinimum' && (
							<Error mb={3}>
								{props.minimumQuestions === 0
									? t('errors.feedbackRequests.minimumQuestion')
									: t('errors.feedbackRequests.minimumQuestions', {
											minimumQuestions: props.minimumQuestions ?? 1,
									  })}
							</Error>
						)}

						{validationError === 'emptyQuestions' && (
							<Error mb={3}>{t('errors.feedbackRequests.emptyQuestion')}</Error>
						)}
					</>
				)}
			</Modal.Contents>
			<Modal.FormFooter
				onCancel={() => props.onBack()}
				saveTitle={t('common.feedback.nextStepRecipients')}
				disabled={!props.template && !!props.needsTemplate}
				onSave={() => {
					if (validationError) {
						setShouldValidate(true);
					} else {
						props.onDone();
					}
				}}
			/>
		</Modal>
	);
}
